import { useState, useRef, useEffect, useCallback } from 'react';
import { Card, Image, Icon, Checkbox, Button } from 'semantic-ui-react';

import { useClothingItems } from '../../hooks/useClothingItems';
import { getThumbnailUrl } from '../../utils/imageUrl';
import { useShare } from '../../context/ShareContext';
import SelectionActionBar from '../selection-action-bar/SelectionActionBar';
import CameraCapture from '../camera-capture/CameraCapture';

import './ClothingCardsGrid.css';

// Helper function to get the display image URL based on available images
// Priority: generatedProductPhoto > removedBgImage (if toggled) > original image (with Cloudinary thumbnail)
const getDisplayImageUrl = (item, useThumbnail = true) => {
  let url;
  // If item has an AI-generated product photo (from source listing), use it as cover
  if (item.generatedProductPhoto?.url) {
    url = item.generatedProductPhoto.url;
  } else if (item.useRemovedImage && item.removedBgImage?.url) {
    // If useRemovedImage is true and removedBgImage exists, use it
    url = item.removedBgImage.url;
  } else {
    // Use the original image URL
    url = item.image?.url || item.image;
  }
  // Apply Cloudinary thumbnail transform (300x300) for grid display
  // This is much better than Strapi's tiny 156px thumbnail format
  return useThumbnail ? getThumbnailUrl(url) : url;
};

// SHEYOU Logo URL
const SHEYOU_LOGO = 'https://res.cloudinary.com/sheyou/image/upload/v1769616249/logo_739166a408.png';

// Upload Photo Card - triggers file picker (accepts multiple)
const UploadPhotoCard = ({ fileInputRef, onFilesSelected }) => {
  const handleClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  return (
    <Card
      className="clothing-card upload-card"
      onClick={handleClick}
      title="Upload photos"
    >
      <input
        ref={fileInputRef}
        type="file"
        hidden
        multiple
        accept="image/*,.heic"
        onChange={onFilesSelected}
      />
      <div className="upload-card-content">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
        <span className="upload-text">upload</span>
      </div>
    </Card>
  );
};

// Take Photo Card - opens camera overlay
const TakePhotoCard = ({ onClick }) => {
  return (
    <Card
      className="clothing-card upload-card"
      onClick={onClick}
      title="Take a photo"
    >
      <div className="upload-card-content">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
          <circle cx="12" cy="13" r="4" />
        </svg>
        <span className="upload-text">camera</span>
      </div>
    </Card>
  );
};

// Browser Extension Card - shown only on desktop (hidden on mobile via CSS)
const BrowserExtensionCard = ({ onClick }) => {
  return (
    <Card
      className="clothing-card browser-extension-card"
      onClick={onClick}
      title="Get browser extension"
    >
      <div className="action-card-content">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#00b5ad" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
          <polyline points="14 2 14 8 20 8" />
          <path d="M12 18v-6" />
          <path d="M9 15l3-3 3 3" />
        </svg>
        <span className="action-card-text">Add Browser Extension</span>
      </div>
    </Card>
  );
};

const ClothingCardsGrid = ({
  onCardClick,
  onDragStart,
  onDragEnd,
  isItemSelectedForTryOn,
  canSelectForTryOn,
  disabled = false,
  // Bulk selection props
  isBulkSelectMode = false,
  selectedItems = [],
  onItemSelect,
  onAddToCloset,
  onBulkDelete,
  // Clothing items passed from parent (for closet filtering)
  clothingItems: propClothingItems = null,
  // Loading state from parent (for closet switching)
  isLoading: propIsLoading = false,
  // Panel position and bulk edit handler
  panelPosition = 'right',
  onBulkEditClick,
  hasClothingItems = true,
  // Clothing modal handler
  onOpenClothingModal,
  // New feature modal handlers
  onOpenEmailModal,
  onOpenExtensionModal,
  // URL import modal handler
  onOpenUrlImportModal,
  // Scroll to a specific item by ID (expands visible count if needed)
  scrollToItemId
}) => {


  // Use RTK Query for clothing data (fallback if no items passed)
  const {
    clothingItems: hookClothingItems,
    isLoading: hookIsLoading,
    error,
    refetch
  } = useClothingItems();

  // Use prop items if provided, otherwise use hook items
  const clothingItems = propClothingItems !== null ? propClothingItems : hookClothingItems;
  // Use prop loading state if items are passed from parent, otherwise use hook loading state
  const isLoading = propClothingItems !== null ? propIsLoading : hookIsLoading;

  const { openShare } = useShare();

  // Share the current bulk selection. Items materialised from a shared look
  // carry a sourceVtoResult, so they are shared onward as looks rather than as
  // flat garments — the recipient gets the outfit image, not just the pieces.
  const handleShareSelection = useCallback(() => {
    if (!selectedItems.length) return;

    const selected = clothingItems.filter((item) => selectedItems.includes(item.id));

    const vtoResultIds = selected
      .map((item) => item.sourceVtoResult?.id || item.sourceVtoResult)
      .filter(Boolean);
    const clothesIds = selected
      .filter((item) => !item.sourceVtoResult)
      .map((item) => item.id);

    openShare({
      sourceType: 'selection',
      clothesIds,
      vtoResultIds,
      itemCount: selected.length,
      previewImages: selected.map((item) => getDisplayImageUrl(item))
    });
  }, [selectedItems, clothingItems, openShare]);

  const containerRef = useRef(null);
  const uploadFileInputRef = useRef(null);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [visibleCount, setVisibleCount] = useState(24); // Progressive rendering
  const isLoadingMoreRef = useRef(false);
  const scrollToItemIdRef = useRef(null);

  // Handle file selection from upload card (single or multiple)
  const handleFilesSelected = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    if (files.length === 1) {
      // Single file - open clothing modal with the file
      if (onOpenClothingModal) {
        onOpenClothingModal(null, files[0]);
      }
    } else {
      // Multiple files - store them and open modal for batch upload
      window.__pendingClothingFiles = files;
      if (onOpenClothingModal) {
        onOpenClothingModal();
      }
    }
  }, [onOpenClothingModal]);

  // Handle photo captured from camera
  const handleCameraCapture = useCallback((file) => {
    if (!file) return;
    if (onOpenClothingModal) {
      onOpenClothingModal(null, file);
    }
  }, [onOpenClothingModal]);

  // A newly acquired item should be visible even when the user was scrolled down.
  const previousFirstItem = useRef(null);
  useEffect(() => {
    const firstId = clothingItems?.[0]?.id;
    if (firstId && previousFirstItem.current && firstId !== previousFirstItem.current) {
      const grid = containerRef.current;
      if (grid) grid.scrollTop = 0;
      const panel = grid?.closest('.clothing-panel-content');
      if (panel) panel.scrollTop = 0;
    }
    previousFirstItem.current = firstId;
  }, [clothingItems]);

  // Handle scrollToItemId: expand visibleCount if needed, then scroll
  useEffect(() => {
    if (!scrollToItemId || !clothingItems || clothingItems.length === 0) return;
    // Avoid re-triggering for the same ID
    if (scrollToItemIdRef.current === scrollToItemId) return;
    scrollToItemIdRef.current = scrollToItemId;

    const itemIndex = clothingItems.findIndex(item => String(item.id) === String(scrollToItemId));
    if (itemIndex === -1) return;

    // +4 accounts for upload + explore + browser + email cards at the start
    const neededCount = itemIndex + 5;
    if (neededCount > visibleCount) {
      setVisibleCount(neededCount);
    }

    // Wait for render, then scroll
    setTimeout(() => {
      const cardElement = document.querySelector(`[data-clothing-id="${scrollToItemId}"]`);
      if (cardElement) {
        cardElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        cardElement.classList.add('highlight-pulse');
        setTimeout(() => cardElement.classList.remove('highlight-pulse'), 1500);
      }
    }, 100);
  }, [scrollToItemId, clothingItems, visibleCount]);


  const handleScroll = useCallback(() => {
    const grid = containerRef.current;
    // Also check the parent .clothing-panel-content which scrolls on mobile
    const parent = grid?.closest('.clothing-panel-content');
    const gridScrolled = grid ? grid.scrollTop > 80 : false;
    const parentScrolled = parent ? parent.scrollTop > 80 : false;
    setShowScrollTop(gridScrolled || parentScrolled);

    // Infinite scroll: load more when near bottom
    const scrollEl = grid || parent;
    if (scrollEl && !isLoadingMoreRef.current) {
      const scrollPosition = Math.ceil(scrollEl.scrollTop + scrollEl.clientHeight);
      const scrollThreshold = scrollEl.scrollHeight - 100;
      if (scrollPosition >= scrollThreshold) {
        isLoadingMoreRef.current = true;
        setVisibleCount(prev => prev + 24);
        setTimeout(() => {
          isLoadingMoreRef.current = false;
        }, 300);
      }
    }
  }, []);

  useEffect(() => {
    const grid = containerRef.current;
    const parent = grid?.closest('.clothing-panel-content');
    if (grid) {
      grid.addEventListener('scroll', handleScroll, { passive: true });
    }
    if (parent) {
      parent.addEventListener('scroll', handleScroll, { passive: true });
    }
    return () => {
      if (grid) grid.removeEventListener('scroll', handleScroll);
      if (parent) parent.removeEventListener('scroll', handleScroll);
    };
    // Re-run when loading completes so ref is available
  }, [handleScroll, isLoading, clothingItems]);

  const scrollToTop = () => {
    const grid = containerRef.current;
    const parent = grid?.closest('.clothing-panel-content');
    grid?.scrollTo({ top: 0, behavior: 'smooth' });
    parent?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCardClick = (item, e) => {
    // In bulk select mode, toggle selection instead of normal click
    if (isBulkSelectMode) {
      if (onItemSelect) {
        onItemSelect(item.id);
      }
      return;
    }

    if (onCardClick) {
      onCardClick(item, e);
    }
  };

  const handleCheckboxClick = (item, e) => {
    e.stopPropagation();
    if (onItemSelect) {
      onItemSelect(item.id);
    }
  };

  const isItemSelected = (itemId) => {
    return selectedItems.includes(itemId);
  };

  const handleEditClick = (item, e) => {
    e.stopPropagation();

    // Open clothing modal in edit mode
    if (onOpenClothingModal) {
      onOpenClothingModal(item.id);
    }
  };

  // Handle loading state - skeleton shimmer
  if (isLoading) {
    return (
      <div className="clothing-cards-container">
        <div className="clothing-cards-grid no-header skeleton-grid">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="skeleton-card">
              <div className="skeleton-shimmer" />
            </div>
          ))}
        </div>
        <div className="skeleton-loading-text">Loading your closet…</div>
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className="clothing-cards-container">
        <div className="error-state">
          <div className="error-message">
            <Icon name="exclamation triangle" color="red" size="large" />
            <h3>Failed to load clothing items</h3>
            <p>{error.message || 'Something went wrong while loading your clothes.'}</p>
            <Button
              color="teal"
              basic
              size="small"
              onClick={() => refetch()}
            >
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Handle empty state
  if (!clothingItems || clothingItems.length === 0) {
    return (
      <div className="clothing-cards-container">
        <div className="empty-state">
          <div className="empty-message">
            <h3>No clothing items found</h3>
            <p>Add some items to your closet to get started!</p>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div
      className="clothing-cards-container"
      onDragOver={(e) => {
        // Only show drop indicator for external file drops
        if (e.dataTransfer.types.includes('Files')) {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'copy';
          e.currentTarget.classList.add('file-drag-over');
        }
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
          e.currentTarget.classList.remove('file-drag-over');
        }
      }}
      onDrop={(e) => {
        e.currentTarget.classList.remove('file-drag-over');
        const files = e.dataTransfer.files;
        if (files.length > 0 && files[0].type.startsWith('image/')) {
          e.preventDefault();
          e.stopPropagation();
          if (onOpenClothingModal) {
            onOpenClothingModal(null, files[0]);
          }
        }
      }}
    >
      {/* Clothing Items Grid - upload and import cards as first items */}
      <div ref={containerRef} className={`clothing-cards-grid no-header position-${panelPosition} ${isBulkSelectMode ? 'with-action-panel' : ''}`}>

        {!isBulkSelectMode && (
          <div className="upload-import-wrapper">
            <UploadPhotoCard fileInputRef={uploadFileInputRef} onFilesSelected={handleFilesSelected} />
            <TakePhotoCard onClick={() => setIsCameraOpen(true)} />
          </div>
        )}

        {/* Browser Extension Card - desktop only (hidden on mobile via CSS) */}
        {!isBulkSelectMode && onOpenExtensionModal && (
          <BrowserExtensionCard onClick={onOpenExtensionModal} />
        )}

        {/* Camera Capture Overlay */}
        <CameraCapture
          open={isCameraOpen}
          onCapture={handleCameraCapture}
          onClose={() => setIsCameraOpen(false)}
        />

        {clothingItems.slice(0, visibleCount).map((item) => {
          const isSelectedForTryOn = isItemSelectedForTryOn && isItemSelectedForTryOn(item.id);
          const isBulkSelected = isItemSelected(item.id);
          const isDisabled = disabled;
          const tooltipText = isBulkSelectMode
            ? (isBulkSelected ? 'Click to deselect' : 'Click to select')
            : (isSelectedForTryOn ? 'Click to remove from outfit' : 'Click to add to outfit');

          return (
            <Card
              key={item.id}
              data-clothing-id={item.id}
              onClick={(e) => !isDisabled && handleCardClick(item, e)}
              draggable={!isDisabled && !isBulkSelectMode}
              onDragStart={(e) => {
                if (isDisabled || isBulkSelectMode) {
                  e.preventDefault();
                  return;
                }
                e.currentTarget.classList.add('dragging');
                onDragStart && onDragStart(e, item);
              }}
              onDragEnd={(e) => {
                e.currentTarget.classList.remove('dragging');
                onDragEnd && onDragEnd(e);
              }}
              className={`clothing-card ${isSelectedForTryOn ? 'selected' : ''} ${isBulkSelected ? 'bulk-selected' : ''} ${isDisabled ? 'disabled' : ''} ${isBulkSelectMode ? 'bulk-mode' : ''}`}
              style={{
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                opacity: isDisabled ? 0.6 : 1,
                pointerEvents: isDisabled ? 'none' : 'auto'
              }}
              title={tooltipText}
            >
              <Image
                src={getDisplayImageUrl(item)}
                wrapped
                ui={false}
                loading="lazy"
                alt={item.clothesName || 'Clothing item'}
                onError={(e) => {
                  e.target.src = '/placeholder-clothing.png';
                }}
              />
              {/* Bulk selection checkbox */}
              {isBulkSelectMode && (
                <div
                  className="bulk-checkbox-wrapper"
                  onClick={(e) => handleCheckboxClick(item, e)}
                >
                  <Checkbox
                    checked={isBulkSelected}
                    onChange={() => { }}
                  />
                </div>
              )}
              {/* Selected state: teal gradient fade + checkmark at bottom */}
              {isSelectedForTryOn && canSelectForTryOn && !isBulkSelectMode && (
                <div className="selected-gradient-overlay">
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <path d="M3 8L7 12L13 4" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              )}
              {/* Edit badge - bottom right (hide when selected or bulk mode) */}
              {!isSelectedForTryOn && !isBulkSelectMode && (
                <div
                  className="edit-badge"
                  onClick={(e) => handleEditClick(item, e)}
                >
                  <svg width="10" height="10" viewBox="0 0 14 14" fill="none">
                    <path d="M10 1.5L12.5 4L4.5 12L1.5 12.5L2 9.5Z" stroke="#fff" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              )}
              {/* SHEYOU source badge - lower left for items from SHEYOU listings */}
              {item.source_listing && !isBulkSelectMode && (
                <div
                  className="sheyou-source-badge"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const listingId = item.source_listing.id || item.source_listing;
                    const sheyouBase = window.location.hostname === 'localhost'
                      ? (process.env.REACT_APP_sheyou_baseUrl_localTest || 'http://localhost:3000')
                      : (process.env.REACT_APP_sheyou_baseUrl_live || 'https://sheyou.co');
                    window.open(`${sheyouBase}/product/${listingId}`, '_blank');
                  }}
                  title="View on SHEYOU"
                  style={{
                    position: 'absolute',
                    bottom: '10px',
                    left: '10px',
                    width: '32px',
                    height: '32px',
                    cursor: 'pointer',
                    zIndex: 25,
                    background: 'white',
                    borderRadius: '50%',
                    padding: '4px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease',
                    border: '1.5px solid #eee'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.1)';
                    e.currentTarget.style.borderColor = '#00b5ad';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.borderColor = '#eee';
                  }}
                >
                  <img
                    src={SHEYOU_LOGO}
                    alt="SHEYOU"
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                </div>
              )}
            </Card>
          );
        })}

      </div>



      {/* Scroll to top button - positioned absolutely over the grid */}
      {showScrollTop && (
        <button className="scroll-to-top-btn" onClick={scrollToTop} title="Scroll to top">
          <Icon name="chevron up" />
        </button>
      )}

      {/* Floating action panel for bulk edit mode */}
      {isBulkSelectMode && (
        <SelectionActionBar
          count={selectedItems.length}
          actions={[
            { key: 'closet', icon: 'folder open outline', label: 'closet', onClick: onAddToCloset },
            { key: 'share', icon: 'share alternate', label: 'share', onClick: handleShareSelection }
          ]}
          destructive={{ icon: 'trash alternate outline', label: 'delete', onClick: onBulkDelete }}
        />
      )}


    </div>
  );
};

export default ClothingCardsGrid;